import React, { useCallback, useState } from 'react';
import styled from 'styled-components';
import { Flex, Typo, CourseGridCard, SimpleButton, ListCarousel, theme } from '../../ui';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { enqueueSnackbar } from 'notistack';
import { selectCurrentStoreHideVat } from '../../features/webshop/globalSlice';
import { addItemToCart, selectCartItemsIds } from '../../features/webshop/cartSlice';
import { useNavigate } from 'react-router-dom';
import { useIsAdmin, useIsIncarnated, useUser } from '../../features/auth/hooks';
import { TOAST_ERROR } from '../../utils/constants';
import { getUserThemeColor, themeGradientBg } from '../../ui/theme/colors';
import aiSwedenLogo from '../../ui/assets/AiSwendenLogo.png';
import PageSection from '../../ui/Page/PageSection';
import mockTeacherData from '../../features/dashboard/components/teacher/mockTeacherData';

const ProviderLogo = styled.img`
  height: 48px;
  width: 48px;
  border-radius: 50%;
  object-fit: cover;
  background: ${theme.colors.NEU_0};
`;

const LogoPlaceholder = styled.div`
  height: 48px;
  width: 48px;
  border-radius: 50%;
  background: ${theme.colors.NEU_0};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 18px;
  color: ${({ $color }) => $color || theme.colors.NEU_800};
`;

const MOCK_UPSELL_PROVIDERS = [
  {
    id: 'nordstrom-academy',
    name: 'Nordström Academy',
    logo: 'https://i.pravatar.cc/96?u=nordstrom',
    themeColor: '#8B2A2A',
    courses: [
      {
        id: 'upsell-na-1',
        name: 'Strategic sales for B2B growth',
        description: 'Learn how to build scalable sales pipelines and close enterprise deals with confidence.',
        ownerName: 'Nordström Academy',
        ownerSlug: 'nordstrom-academy',
        slug: 'strategic-sales-b2b-growth',
        public: 1,
        currency: 'SEK',
        price: { gross: 14900, net: 14900, discounted: 14900 },
        coverImage: [{ url: 'https://picsum.photos/400/250?random=50' }],
        categories: [{ slug: 'sales', name: 'Sales' }],
        relevanceScore: 96,
        type: 1,
        courses: Array.from({ length: 2 }),
      },
      {
        id: 'upsell-na-2',
        name: 'Data-driven decision making',
        description: 'Transform raw data into actionable business insights using modern analytics frameworks.',
        ownerName: 'Nordström Academy',
        ownerSlug: 'nordstrom-academy',
        slug: 'data-driven-decisions',
        public: 1,
        currency: 'SEK',
        price: { gross: 12500, net: 12500, discounted: 12500 },
        coverImage: [{ url: 'https://picsum.photos/400/250?random=51' }],
        categories: [{ slug: 'analytics', name: 'Analytics' }],
        relevanceScore: 91,
        type: 2,
        courses: Array.from({ length: 4 }),
      },
      {
        id: 'upsell-na-3',
        name: 'Leadership in fast-growing companies',
        description: 'Navigate the unique challenges of leading teams through rapid organizational growth.',
        ownerName: 'Nordström Academy',
        ownerSlug: 'nordstrom-academy',
        slug: 'leadership-fast-growing',
        public: 1,
        currency: 'SEK',
        price: { gross: 18900, net: 18900, discounted: 18900 },
        coverImage: [{ url: 'https://picsum.photos/400/250?random=52' }],
        categories: [{ slug: 'leadership', name: 'Leadership' }],
        relevanceScore: 88,
        type: 1,
        courses: Array.from({ length: 3 }),
      },
      {
        id: 'upsell-na-4',
        name: 'Customer success fundamentals',
        description: 'Build a world-class customer success practice that drives retention and expansion revenue.',
        ownerName: 'Nordström Academy',
        ownerSlug: 'nordstrom-academy',
        slug: 'customer-success-fundamentals',
        public: 1,
        currency: 'SEK',
        price: { gross: 9800, net: 9800, discounted: 9800 },
        coverImage: [{ url: 'https://picsum.photos/400/250?random=53' }],
        categories: [{ slug: 'business', name: 'Business' }],
        relevanceScore: 85,
      },
    ],
  },
  {
    id: 'greenhouse-learning',
    name: 'Greenhouse Learning',
    logo: 'https://i.pravatar.cc/96?u=greenhouse',
    themeColor: '#1A6B4A',
    courses: [
      {
        id: 'upsell-gl-1',
        name: 'Sustainable business strategy',
        description: 'Integrate sustainability into your core business model for long-term competitive advantage.',
        ownerName: 'Greenhouse Learning',
        ownerSlug: 'greenhouse-learning',
        slug: 'sustainable-business-strategy',
        public: 1,
        currency: 'EUR',
        price: { gross: 199, net: 199, discounted: 199 },
        coverImage: [{ url: 'https://picsum.photos/400/250?random=60' }],
        categories: [{ slug: 'sustainability', name: 'Sustainability' }],
        type: 1,
        courses: Array.from({ length: 5 }),
      },
      {
        id: 'upsell-gl-2',
        name: 'Circular economy in practice',
        description: 'Design products and services that eliminate waste and keep materials in use longer.',
        ownerName: 'Greenhouse Learning',
        ownerSlug: 'greenhouse-learning',
        slug: 'circular-economy-practice',
        public: 1,
        currency: 'EUR',
        price: { gross: 149, net: 149, discounted: 149 },
        coverImage: [{ url: 'https://picsum.photos/400/250?random=61' }],
        categories: [{ slug: 'sustainability', name: 'Sustainability' }],
      },
      {
        id: 'upsell-gl-3',
        name: 'ESG reporting for managers',
        description: 'Understand environmental, social, and governance frameworks and how to report on them effectively.',
        ownerName: 'Greenhouse Learning',
        ownerSlug: 'greenhouse-learning',
        slug: 'esg-reporting-managers',
        public: 1,
        currency: 'EUR',
        price: { gross: 179, net: 179, discounted: 179 },
        coverImage: [{ url: 'https://picsum.photos/400/250?random=62' }],
        categories: [{ slug: 'compliance', name: 'Compliance' }],
        type: 2,
        courses: Array.from({ length: 4 }),
      },
    ],
  },
  {
    id: 'atlas-digital',
    name: 'Atlas Digital Institute',
    logo: null,
    themeColor: '#2C5F7C',
    courses: [
      {
        id: 'upsell-ad-1',
        name: 'AI for product managers',
        description: 'Learn to evaluate, scope, and ship AI-powered features without a machine learning background.',
        ownerName: 'Atlas Digital Institute',
        ownerSlug: 'atlas-digital',
        slug: 'ai-product-managers',
        public: 1,
        currency: 'USD',
        price: { gross: 249, net: 249, discounted: 249 },
        coverImage: [{ url: 'https://picsum.photos/400/250?random=70' }],
        categories: [{ slug: 'technology', name: 'Technology' }],
        type: 1,
        courses: Array.from({ length: 12 }),
      },
      {
        id: 'upsell-ad-2',
        name: 'Cloud architecture patterns',
        description: 'Design resilient, cost-effective cloud infrastructure using battle-tested architectural patterns.',
        ownerName: 'Atlas Digital Institute',
        ownerSlug: 'atlas-digital',
        slug: 'cloud-architecture-patterns',
        public: 1,
        currency: 'USD',
        price: { gross: 299, net: 299, discounted: 299 },
        coverImage: [{ url: 'https://picsum.photos/400/250?random=71' }],
        categories: [{ slug: 'technology', name: 'Technology' }],
        type: 2,
        courses: Array.from({ length: 8 }),
      },
      {
        id: 'upsell-ad-3',
        name: 'Cybersecurity essentials',
        description: 'Protect your organization from modern threats with practical security knowledge for every role.',
        ownerName: 'Atlas Digital Institute',
        ownerSlug: 'atlas-digital',
        slug: 'cybersecurity-essentials',
        public: 1,
        currency: 'USD',
        price: { gross: 189, net: 189, discounted: 189 },
        coverImage: [{ url: 'https://picsum.photos/400/250?random=72' }],
        categories: [{ slug: 'security', name: 'Security' }],
      },
    ],
  },
];

const AI_SWEDEN_PROVIDER = {
  id: 'ai-sweden',
  name: 'AI Sweden',
  logo: aiSwedenLogo,
  themeColor: '#202F5B',
  courses: [
    {
      id: 'upsell-ais-1',
      name: 'Applied AI for industry leaders',
      description: 'Understand how to identify, evaluate, and implement AI solutions that drive measurable business value.',
      ownerName: 'AI Sweden',
      ownerSlug: 'ai-sweden',
      slug: 'applied-ai-industry-leaders',
      public: 1,
      currency: 'SEK',
      price: { gross: 24900, net: 24900, discounted: 24900 },
      coverImage: [{ url: 'https://picsum.photos/400/250?random=80' }],
      categories: [{ slug: 'artificial-intelligence', name: 'Artificial Intelligence' }],
      type: 1,
      courses: Array.from({ length: 6 }),
    },
    {
      id: 'upsell-ais-2',
      name: 'Responsible AI & ethics',
      description: 'Build trustworthy AI systems with frameworks for fairness, transparency, and accountability.',
      ownerName: 'AI Sweden',
      ownerSlug: 'ai-sweden',
      slug: 'responsible-ai-ethics',
      public: 1,
      currency: 'SEK',
      price: { gross: 15900, net: 15900, discounted: 15900 },
      coverImage: [{ url: 'https://picsum.photos/400/250?random=81' }],
      categories: [{ slug: 'ai-ethics', name: 'AI Ethics' }],
      type: 2,
      courses: Array.from({ length: 4 }),
    },
    {
      id: 'upsell-ais-3',
      name: 'Natural language processing in practice',
      description: 'From text classification to large language models — learn to build NLP solutions for real-world use cases.',
      ownerName: 'AI Sweden',
      ownerSlug: 'ai-sweden',
      slug: 'nlp-in-practice',
      public: 1,
      currency: 'SEK',
      price: { gross: 19900, net: 19900, discounted: 19900 },
      coverImage: [{ url: 'https://picsum.photos/400/250?random=82' }],
      categories: [{ slug: 'machine-learning', name: 'Machine Learning' }],
      type: 1,
      courses: Array.from({ length: 8 }),
    },
    {
      id: 'upsell-ais-4',
      name: 'Computer vision for automation',
      description: 'Deploy image recognition and video analysis models to automate quality control and monitoring.',
      ownerName: 'AI Sweden',
      ownerSlug: 'ai-sweden',
      slug: 'computer-vision-automation',
      public: 1,
      currency: 'SEK',
      price: { gross: 21500, net: 21500, discounted: 21500 },
      coverImage: [{ url: 'https://picsum.photos/400/250?random=83' }],
      categories: [{ slug: 'computer-vision', name: 'Computer Vision' }],
    },
  ],
};

const ProviderHeader = ({ provider }) => {
  const mockedDataProvider = { name: 'Mocked data', themeColor: 'pink' };
  const initial = provider?.name?.charAt(0)?.toUpperCase() || 'M';

  return (
    <Flex align='center' gap='12' width='100%'>
      {provider?.logo ? (
        <ProviderLogo src={provider?.logo} alt={provider?.name} />
      ) : (
        <LogoPlaceholder $color={provider?.themeColor || mockedDataProvider.themeColor}>{initial}</LogoPlaceholder>
      )}
      <Flex direction='column' gap='2' align='flex-start'>
        <Typo.Info $caps $themeColor={provider?.themeColor || mockedDataProvider.themeColor} style={{ opacity: 0.8 }}>
          Network Partner
        </Typo.Info>
        <Typo.ContentSmallHeader $themeColor={provider?.themeColor || mockedDataProvider.themeColor}>
          {provider?.name || mockedDataProvider.name}
        </Typo.ContentSmallHeader>
      </Flex>
    </Flex>
  );
};

/**
 * NetworkUpsells Component
 *
 * Displays campaign-based course upsells from network partners.
 * Each provider section is branded with the store owner's colors.
 * Uses mock data until the BE is ready.
 */
const NetworkUpsells = () => {
  const [showMockPreview, setShowMockPreview] = useState(false);
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const hideVAT = useSelector(selectCurrentStoreHideVat);
  const cartItems = useSelector(selectCartItemsIds);
  const isAdmin = useIsAdmin();
  const isIncarnated = useIsIncarnated();
  const canPreview = isAdmin || isIncarnated;
  const user = useUser();

  // TODO: Replace with real API call when BE is ready
  // User 95 always sees AI Sweden; others get daily rotation of other providers
  const [selectedProvider] = useState(() => {
    if (user?.id === 95) return AI_SWEDEN_PROVIDER;
    return MOCK_UPSELL_PROVIDERS[new Date().getDate() % MOCK_UPSELL_PROVIDERS.length];
  });
  const upsellProviders = showMockPreview ? [selectedProvider] : [];

  const handleCardClick = useCallback(
    (course) => {
      const isBundle = course.type === 2;
      const ownerSlug = course.ownerSlug || course.owner?.username;
      navigate(`/store/${ownerSlug}/${isBundle ? 'package/' : ''}${course.slug}`, { state: course });
    },
    [navigate],
  );

  const handleBuy = useCallback(
    (item) => {
      if (cartItems.find((el) => el === item.id)) {
        enqueueSnackbar(t('webshop.alreadyInCart'), { variant: TOAST_ERROR });
      } else if (!!cartItems.length) {
        enqueueSnackbar(t('webshop.canOnlyBuyOne'), { variant: TOAST_ERROR });
      } else {
        const ownerSlug = item.ownerSlug || item.owner?.username;
        dispatch(addItemToCart(item));
        navigate(`/store/${ownerSlug}/checkout`);
      }
    },
    [dispatch, cartItems, t, navigate],
  );

  const isEmpty = upsellProviders.length === 0;

  return (
    <Flex direction='column' align='flex-start' width='100%'>
      {isEmpty ? (
        <Flex direction='column' align='center' gap='16' width='100%'>
          {canPreview && !showMockPreview && (
            <PageSection backgroundColor={themeGradientBg(getUserThemeColor('pink'))}>
              <ProviderHeader />
              <SimpleButton variant='outlined' onClick={() => setShowMockPreview(true)}>
                Show mocked preview
              </SimpleButton>
            </PageSection>
          )}
        </Flex>
      ) : (
        upsellProviders.map((provider) => (
          <PageSection key={provider.id} backgroundColor={themeGradientBg(provider.themeColor)}>
            <ListCarousel header={<ProviderHeader provider={provider} />} navColor={theme.colors.NEU_0} slideWidth={300} spaceBetween={24}>
              {provider.courses.map((course) => (
                <CourseGridCard
                  key={course.id}
                  courseInfo={course}
                  onCardClick={handleCardClick}
                  onBuy={handleBuy}
                  hideVAT={hideVAT}
                  buyLabel={t('coursioNetwork.buyNow')}
                />
              ))}
            </ListCarousel>
          </PageSection>
        ))
      )}
    </Flex>
  );
};

export default NetworkUpsells;
